/**
 * Theme hook — manages theme application based on store settings
 *
 * Adds/removes .light class on document.documentElement.
 * Listens to prefers-color-scheme media query for system mode.
 * Auto-applies Vizi designer theme colors for the current mode.
 */

import { useEffect } from "react";
import { useSnapshot } from "valtio";
import { store } from "./store";
import { applyCustomTheme, getDefaultThemeForMode } from "./theme-utils";

function getEffectiveMode(theme: "light" | "dark" | "system"): "light" | "dark" {
  if (theme === "light") return "light";
  if (theme === "dark") return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function useTheme() {
  const { settings } = useSnapshot(store);
  const { theme } = settings;

  // Apply light/dark class
  useEffect(() => {
    function applyThemeMode(isDark: boolean) {
      if (isDark) {
        document.documentElement.classList.remove("light");
      } else {
        document.documentElement.classList.add("light");
      }
    }

    if (theme === "light") {
      applyThemeMode(false);
      return;
    }

    if (theme === "dark") {
      applyThemeMode(true);
      return;
    }

    // System theme — use media query
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    applyThemeMode(mediaQuery.matches);

    function handleChange(e: MediaQueryListEvent) {
      applyThemeMode(e.matches);
    }

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme]);

  // Auto-apply Vizi designer theme colors for the current mode
  useEffect(() => {
    const effectiveMode = getEffectiveMode(theme);
    const defaultTheme = getDefaultThemeForMode(effectiveMode);
    applyCustomTheme(defaultTheme ?? null);
  }, [theme]);
}
